import type { BackendModule, ReadCallback, Services } from 'i18next';
import apiClient from '@/api/client';
import type { ApiResponse } from '@/api/types';

interface BackendPluginOptions {
  /** Base path for i18n API. Defaults to '/i18n' (relative to apiClient baseURL) */
  apiPath?: string;
}

const cache = new Map<string, Record<string, string>>();

function cacheKey(language: string, namespace: string): string {
  return `${language}:${namespace}`;
}

const backendPlugin: BackendModule<BackendPluginOptions> = {
  type: 'backend',
  init(_services: Services, _backendOptions: BackendPluginOptions) {
    // No-op: options stored if needed for future extension
  },
  read(language: string, namespace: string, callback: ReadCallback) {
    const key = cacheKey(language, namespace);
    const cached = cache.get(key);
    if (cached) {
      callback(null, cached);
      return;
    }

    apiClient
      .get<ApiResponse<Record<string, string>>>(`/i18n/${namespace}`, {
        headers: { 'Accept-Language': language },
      })
      .then((response) => {
        const data = response.data;
        if (data.success && data.data) {
          cache.set(key, data.data);
          callback(null, data.data);
        } else {
          // API returned non-success; fall back to bundled resources
          callback(null, {});
        }
      })
      .catch(() => {
        // Network error or auth failure; fall back to bundled resources
        callback(null, {});
      });
  },
};

/** Clear the in-memory translation cache (e.g., on locale change or logout) */
export function clearTranslationCache(): void {
  cache.clear();
}

export default backendPlugin;
